import { useCallback, useEffect, useMemo, useRef } from 'react';

/** A debounced function that also exposes an imperative way to drop any pending invocation. */
export type DebouncedFunction<T extends (...args: never[]) => void> = ((...args: Parameters<T>) => void) & {
  /** Cancels a pending debounced invocation, if any. Does not invoke the callback. */
  cancel: () => void;
};

/**
 * Returns a debounced version of `callback`, invoked `delay` ms after the last call. The returned
 * function is referentially stable across renders and exposes `.cancel()` to drop a pending call.
 *
 * @param callback - The function to debounce.
 * @param delay - Debounce delay in milliseconds.
 * @returns A debounced wrapper around `callback`, with a `.cancel()` method.
 */
export function useDebouncedCallback<T extends (...args: never[]) => void>(
  callback: T,
  delay: number,
): DebouncedFunction<T> {
  const callbackRef = useRef<T>(callback);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  const debounced = useCallback(
    (...args: Parameters<T>): void => {
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current);
      }
      timerRef.current = setTimeout(() => {
        timerRef.current = null;
        callbackRef.current(...args);
      }, delay);
    },
    [delay],
  );

  const cancel = useCallback((): void => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  return useMemo(() => Object.assign(debounced, { cancel }), [debounced, cancel]);
}
